import { buttonVariants } from '@xhubio-saas/core-frontend/ui-components'
import {
  ApiRequestInspector,
  type ConversionMode,
  DownloadButton,
  hinweisZeile,
  type NormalizedConversionResult,
  type RestApiCallState,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { ExternalLink } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type Finding, groupFindings } from '../../../api/invoiceValidationClient'
import { ContentBlock } from '../validation/ContentBlock'
import { FindingsReport } from '../validation/FindingsReport'
import { pluralCategory } from '../validation/VerdictHeader'
import { Workbench, type WorkbenchPanel } from '../validation/Workbench'
import { type ConversionOutcome, ConversionResultHeader } from './ConversionResultHeader'

/** Der Schluessel, unter dem die Ergebnis-Werkbank ihren Tab merkt. */
export const RESULT_TAB_KEY = 'invoice-conversion:result-tab'

type PackageT = (key: string, params?: Record<string, string>) => string

export interface ConversionResultSectionProps {
  result: NormalizedConversionResult
  /** Die Richtung des Laufs, aus dem das Ergebnis stammt. */
  mode: ConversionMode
  /** Der Name des erzeugten Formats (`PDF`, Katalogname des Ziels, `JSON`). */
  formatLabel: string
  requestState?: RestApiCallState | null
  apiKey?: string | null
  maskedApiKey?: string | null
  tPackage: PackageT
}

/** Bytes eines Ergebnisses: Base64 (PDF) zurueckgerechnet, Text als UTF-8. */
function byteSize(data: string, isBase64: boolean): number {
  if (!isBase64) return new Blob([data]).size
  const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding)
}

/** PDF-Base64 als Blob-URL fuer die Vorschau — freigegeben, wenn sich das Ergebnis aendert. */
function usePdfUrl(base64: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!base64) {
      setUrl(null)
      return
    }
    let created: string | null = null
    try {
      const raw = atob(base64)
      const bytes = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
      created = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      setUrl(created)
    } catch {
      setUrl(null)
    }
    return () => {
      if (created) URL.revokeObjectURL(created)
    }
  }, [base64])
  return url
}

/**
 * **Das Ergebnis eines Laufs** — Kopfzeile, Report der Fehler und Hinweise, Werkbank mit Vorschau/XML/JSON und
 * Anfrage. Erscheint erst nach dem Konvertieren (Plan: kein Leerkasten vorher).
 *
 * 🔴 Fehler (`errors[]`, ParseError mit xpath/Zeile) und Hinweise (`warnings[]`) laufen durch denselben
 * `FindingsReport` wie die Validierung: Kennung · Text, gleiche Meldungen als „2×". Ein Hinweis spricht die Zeile des Pakets
 * (`hinweisZeile`: Katalogsatz mit Regelverweis, ein Normbefund den Normtext, dahinter die Beschriftung des Felds; Torsten
 * Nr. 522) — zwei Hinweise zu zwei Angaben bleiben so zwei Zeilen.
 */
export function ConversionResultSection({
  result,
  mode,
  formatLabel,
  requestState,
  apiKey,
  maskedApiKey,
  tPackage,
}: ConversionResultSectionProps) {
  const { t, i18n } = useTranslation()
  const isPdf = result.success && result.contentType === 'application/pdf'
  const pdfUrl = usePdfUrl(isPdf ? result.data : undefined)

  const errors: Finding[] = useMemo(
    () =>
      (result.success ? [] : (result.errors ?? [])).map((e) => ({
        code: e.code,
        message: e.code === 'MISSING_FORMAT' ? tPackage('error.noFormatSelected') : e.message,
        // Wo im Beleg es klemmt: der XPath, sonst Zeile:Spalte.
        field:
          e.xpath ??
          (e.line !== undefined
            ? `${e.line}${e.column !== undefined ? `:${e.column}` : ''}`
            : undefined),
      })),
    [result, tPackage]
  )
  const warnings: Finding[] = useMemo(
    () =>
      (result.warnings ?? []).map((w) => ({
        code: w.code,
        message: hinweisZeile(w),
        rule: w.rule,
        // Kein `field`: der Report zeigte den Rohpfad (E1) — die Beschriftung steht schon in der Zeile.
      })),
    [result]
  )

  const outcome: ConversionOutcome = !result.success
    ? 'failed'
    : mode === 'toJson'
      ? 'read'
      : 'created'
  const bytes = result.success && result.data ? byteSize(result.data, isPdf) : undefined

  const panels: WorkbenchPanel[] = []
  if (isPdf) {
    panels.push({
      id: 'preview',
      label: t('invoiceConversionPage.result.tab.preview'),
      hint: 'PDF',
      content: pdfUrl ? (
        // Die eingebaute PDF-Anzeige des Browsers. Auf Telefonen zeigt sie oft nichts — dafuer stehen „In neuem Tab
        // oeffnen" und „Herunterladen" in der Tab-Zeile.
        <iframe
          title={tPackage('pdfViewer.title')}
          src={pdfUrl}
          className="block h-[70dvh] min-h-[420px] w-full rounded-md border border-border bg-muted"
          data-testid="invoice-conversion-pdf"
        />
      ) : null,
    })
  }
  const xml = result.success
    ? result.contentType === 'application/pdf'
      ? result.embeddedXml
      : result.contentType?.includes('xml')
        ? result.data
        : undefined
    : undefined
  if (xml) {
    const lines = xml.split('\n').length
    panels.push({
      id: 'xml',
      label: t('invoiceConversionPage.result.tab.xml'),
      content: (
        <ContentBlock
          testId="invoice-conversion-xml"
          collapsible={false}
          title={
            isPdf ? tPackage('output.embeddedXmlLabel') : t('invoiceConversionPage.result.xmlTitle')
          }
          chips={[
            {
              text: t('invoiceConversionPage.result.size', {
                kb: Math.max(1, Math.round(new Blob([xml]).size / 1024)),
              }),
            },
            {
              text: t(`invoiceValidation.content.lines.${pluralCategory(i18n.language, lines)}`, {
                n: lines,
              }),
            },
          ]}
          content={xml}
          language="xml"
          fileName={isPdf ? 'embedded.xml' : (result.filename ?? 'invoice.xml')}
          bytes={new Blob([xml]).size}
        />
      ),
    })
  }
  if (result.success && result.data && result.contentType?.includes('json')) {
    const json = result.data
    const lines = json.split('\n').length
    panels.push({
      id: 'json',
      label: t('invoiceConversionPage.result.tab.json'),
      content: (
        <ContentBlock
          testId="invoice-conversion-json"
          collapsible={false}
          title={t('invoiceConversionPage.result.jsonTitle')}
          chips={[
            result.filename ? { text: result.filename } : undefined,
            {
              text: t(`invoiceValidation.content.lines.${pluralCategory(i18n.language, lines)}`, {
                n: lines,
              }),
            },
          ]}
          content={json}
          language="json"
          fileName={result.filename ?? 'invoice.json'}
          bytes={new Blob([json]).size}
        />
      ),
    })
  }
  // Nur mit einer Anfrage: scheitert der Lauf schon im Browser (kaputtes JSON), gibt es keine zu zeigen.
  if (requestState?.lastRequest) {
    panels.push({
      id: 'request',
      label: t('invoiceConversionPage.result.tab.request'),
      content: (
        // Uebergang wie in der Validierung: der Kopf des Inspectors laeuft bei 375 px ueber den Rand
        // (Issue `2026-10-10_api-inspector-kopf-mobil-und-leerraum.md`).
        <div
          // Weisse Flaeche auf der grauen Seite (Dashboard-Ruhe): die Huelle des Inspectors
          // (invoice-frontend) hat keinen eigenen Hintergrund.
          className="min-w-0 max-w-full overflow-hidden rounded-md bg-card"
          data-testid="invoice-conversion-inspector"
        >
          <ApiRequestInspector
            request={requestState.lastRequest}
            response={requestState.lastResponse}
            apiKey={apiKey ?? ''}
            maskedApiKey={maskedApiKey ?? '***'}
            t={tPackage as (key: string) => string}
          />
        </div>
      ),
    })
  }

  const actions = result.success ? (
    <>
      {isPdf && pdfUrl && (
        <a
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          className={buttonVariants({
            variant: 'outline',
            size: 'sm',
            className: 'whitespace-nowrap',
          })}
          data-testid="invoice-conversion-open-pdf"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {tPackage('pdfViewer.openInTab')}
        </a>
      )}
      <DownloadButton result={result} t={tPackage as (key: string) => string} />
    </>
  ) : undefined

  return (
    <section
      className="min-w-0 space-y-4"
      aria-label={t('invoiceConversionPage.result.title')}
      data-testid="invoice-conversion-result"
    >
      <ConversionResultHeader
        outcome={outcome}
        formatLabel={formatLabel}
        bytes={bytes}
        warningCount={groupFindings(warnings).length}
        errorCount={groupFindings(errors).length}
        quotaExceeded={Boolean(result.quota)}
      />
      {/* Ein Fehler ohne Liste (Netz, Zod, Servertext) steht als Satz unter der Kopfzeile. */}
      {!result.success && !result.quota && errors.length === 0 && result.error && (
        <p
          className="whitespace-pre-line text-sm [overflow-wrap:anywhere]"
          role="alert"
          data-testid="invoice-error"
        >
          {result.error}
        </p>
      )}
      <FindingsReport findings={errors} warnings={warnings} />
      {panels.length > 0 && (
        <Workbench
          panels={panels}
          storageKey={RESULT_TAB_KEY}
          testIdPrefix="invoice-conversion-result"
          label={t('invoiceConversionPage.result.title')}
          actions={actions}
        />
      )}
    </section>
  )
}
